import { useContext, useState, useEffect, useMemo } from "react";
import inputStyle from "../../styles/input.module.scss";
import { MaterialContext } from "../../context/MaterialContext";
import axios from "axios";
import DragDrop from "./DragDrop";
import Dropzone from "./Dropzone";
import { closeIcon } from "../../components/Icons";
import PreviewSwatch from "./PreviewSwatch";
import ImagePreviewFrame from "./ImagePreviewFrame";

function ImageAdd({
  vendor,
  slug,
  data,
  setMaterialArray,
  materialDescription,
}) {
  const { ROOT_SERVER } = useContext(MaterialContext);
  const [images, setImages] = useState([]);
  const [folder, setFolder] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [uploadedImages, setUploadedImages] = useState(false);

  useEffect(() => {
    setMaterialArray(images);
  }, [images, setMaterialArray]);

  function getData({ refreshFolderOnly = false } = {}) {
    const materialGet = `${ROOT_SERVER}material/${vendor}/${slug}`;
    const folderGet = `${ROOT_SERVER}material/folder/${vendor}/${slug}`;

    if (refreshFolderOnly) {
      axios
        .get(folderGet)
        .then((response) => {
          if (Array.isArray(response.data) && response.data.length) {
            setFolder(response.data);
          }
        })
        .catch((errors) => {
          console.log(errors);
        });
      return;
    }

    axios
      .all([axios.get(materialGet), axios.get(folderGet)])
      .then(
        axios.spread((responseMaterials, responseFolder) => {
          const material = responseMaterials.data?.[0];
          if (material?.images) {
            setImages(material.images);
            setPreviewImage(material.images[0] || "");
          }
          if (Array.isArray(responseFolder.data) && responseFolder.data.length) {
            setFolder(responseFolder.data);
          }
        }),
      )
      .catch((errors) => {
        console.log(errors);
      });
  }

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [data, vendor, slug]);

  useEffect(() => {
    if (uploadedImages) {
      getData({ refreshFolderOnly: true });
      setUploadedImages(false);
    }
    // eslint-disable-next-line
  }, [uploadedImages]);

  const unusedImages = useMemo(
    () => folder.filter((item) => !images.includes(item)),
    [folder, images],
  );

  const handleAddImageFromFolder = (image) => {
    setImages((current) =>
      current.includes(image) ? current : [...current, image],
    );
    setPreviewImage(image);
  };

  const handleUploadSuccess = (uploadedNames) => {
    const nextNames = Array.isArray(uploadedNames) ? uploadedNames : [];
    // Keep uploads in staging (folder / unused). Do not auto-add to the swatch.
    if (nextNames[0]) {
      setPreviewImage(nextNames[0]);
    }
  };

  const handleDeleteFromFolder = (item) => {
    const confirmed = window.confirm(
      `Delete ${item} from the folder permanently? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }

    axios
      .delete(`${ROOT_SERVER}upload/delete/${item}`, {
        headers: {
          "Content-Type": "application/json",
        },
        data: { vendor: vendor, slug: slug },
      })
      .then(() => {
        getData();
      });
  };

  return (
    <div className={inputStyle.imageWorkspace}>
      <div className={inputStyle.imageColumns}>
        <section className={inputStyle.imageColumn}>
          <div className={inputStyle.previewHeader}>
            <div className={inputStyle.previewSlot}>
              <p className={inputStyle.imagePreviewLabel}>Swatch preview</p>
              <div className={inputStyle.swatchView}>
                <PreviewSwatch
                  images={images}
                  vendor={vendor}
                  slug={slug}
                  materialDescription={materialDescription}
                />
              </div>
            </div>
            <div className={inputStyle.previewSlot}>
              <p className={inputStyle.imagePreviewLabel}>Image preview</p>
              <ImagePreviewFrame
                src={
                  previewImage
                    ? `${ROOT_SERVER}${vendor}/${slug}/swatch/${previewImage}`
                    : ""
                }
                alt={previewImage || "swatch preview"}
                emptyLabel="Click an image on the right to preview"
              />
            </div>
          </div>
        </section>

        <section className={inputStyle.imageColumn}>
          <p className={inputStyle.imageWorkflowNote}>
            Upload into staging, then push images into the swatch when you are
            ready.
          </p>

          <div className={inputStyle.imageSectionHeader}>
            <h4 className={inputStyle.imageSectionHeading}>
              In this swatch
            </h4>
            <p className={inputStyle.imageSectionNote}>
              These images are shown on the material. Click and drag to change
              order.
            </p>
          </div>
          <DragDrop
            images={images}
            vendor={vendor}
            slug={slug}
            setImages={setImages}
            setPreviewImage={setPreviewImage}
          />

          <div
            className={`${inputStyle.imageSectionHeader} ${inputStyle.imageSectionHeaderSecondary}`}
          >
            <h4 className={inputStyle.imageSectionHeading}>Staging</h4>
            <p className={inputStyle.imageSectionNote}>
              Uploaded and unused folder files wait here. Use Add to swatch to
              push them into view.
            </p>
          </div>
          <div
            className={`${inputStyle.imagePool} ${inputStyle.imagePanel} ${inputStyle.imagePoolSecondary}`}
          >
            {unusedImages.length === 0 ? (
              <div className={inputStyle.dragDropEmpty}>
                Staging is empty — upload below to add files here
              </div>
            ) : (
              unusedImages.map((item, i) => (
                <div key={item} className={inputStyle.dragDropItem}>
                  <div
                    className={inputStyle.deleteDragDropItem}
                    onClick={() => handleDeleteFromFolder(item)}
                    title="Delete from folder"
                  >
                    <div className={inputStyle.deleteIcon}>{closeIcon}</div>
                  </div>
                  <img
                    src={`${ROOT_SERVER}${vendor}/${slug}/thumbnail/${item}`}
                    alt={item + "folder" + i}
                    onClick={() => setPreviewImage(item)}
                  />
                  <button
                    type="button"
                    className={inputStyle.folderItemAdd}
                    onClick={() => handleAddImageFromFolder(item)}
                  >
                    Add to swatch
                  </button>
                </div>
              ))
            )}
          </div>

          <div
            className={`${inputStyle.imageSectionHeader} ${inputStyle.imageSectionHeaderSecondary}`}
          >
            <h4 className={inputStyle.imageSectionHeading}>Upload</h4>
            <p className={inputStyle.imageSectionNote}>
              Drop or browse to stage files. They will not enter the swatch
              until you add them.
            </p>
          </div>
          <div
            className={`${inputStyle.imagePool} ${inputStyle.imagePanel} ${inputStyle.imagePoolCompact}`}
          >
            <Dropzone
              vendor={vendor}
              slug={slug}
              setUploadedImages={setUploadedImages}
              onUploadSuccess={handleUploadSuccess}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

export default ImageAdd;
